/**
 * Cookie Consent Banner
 *
 * GDPR-compliant cookie consent banner
 * Features:
 * - Accept all / Reject all / Customize
 * - Persistent consent storage
 * - Customizable preferences
 * - Multi-language support
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  thirdParty: boolean;
}

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true, // Always true
    analytics: false,
    marketing: false,
    thirdParty: false,
  });

  useEffect(() => {
    // Check if consent has been given
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Apply stored consent settings
      try {
        const storedPrefs = JSON.parse(consent) as ConsentPreferences;
        applyConsentSettings(storedPrefs);
      } catch (error) {
        console.error('Error parsing stored consent:', error);
        setShowBanner(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      thirdParty: true,
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      thirdParty: false,
    };
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs: ConsentPreferences) => {
    localStorage.setItem('cookie_consent', JSON.stringify(prefs));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setShowBanner(false);
    setShowCustomize(false);

    // Apply consent settings
    applyConsentSettings(prefs);
  };

  const applyConsentSettings = (prefs: ConsentPreferences) => {
    // Update gtag consent if available
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
      });
    }

    // Log consent settings for debugging
    console.log('Consent settings applied:', {
      analytics: prefs.analytics ? 'enabled' : 'disabled',
      marketing: prefs.marketing ? 'enabled' : 'disabled',
      thirdParty: prefs.thirdParty ? 'enabled' : 'disabled',
    });
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-2xl">
      {!showCustomize ? (
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('cookies.title')}</h3>
              <p className="text-sm text-gray-600">
                {t('cookies.description')}{' '}
                <Link to="/privacy" className="text-blue-600 underline hover:text-blue-800">
                  {t('cookies.learnMore')}
                </Link>
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowCustomize(true)}
                className="px-4 py-2 min-h-touch text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('cookies.customize')}
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 min-h-touch text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {t('cookies.rejectAll')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 min-h-touch text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('cookies.acceptAll')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('cookies.customizeTitle')}</h3>
            <p className="text-sm text-gray-600">
              {t('cookies.customizeDescription')}
            </p>
          </div>

          <div className="space-y-4 mb-6">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-900">{t('cookies.necessary.title')}</h4>
                  <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">{t('cookies.necessary.required')}</span>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookies.necessary.description')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={true}
                disabled
                className="mt-1 h-5 w-5 text-blue-600 rounded cursor-not-allowed opacity-50"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900">{t('cookies.analytics.title')}</h4>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookies.analytics.description')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={preferences.analytics}
                onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900">{t('cookies.marketing.title')}</h4>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookies.marketing.description')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={preferences.marketing}
                onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Third-Party Cookies */}
            <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900">{t('cookies.thirdParty.title')}</h4>
                <p className="mt-1 text-sm text-gray-600">
                  {t('cookies.thirdParty.description')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={preferences.thirdParty}
                onChange={(e) => setPreferences({ ...preferences, thirdParty: e.target.checked })}
                className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowCustomize(false)}
              className="px-4 py-2 min-h-touch text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('cookies.back')}
            </button>
            <button
              onClick={handleSavePreferences}
              className="px-6 py-2 min-h-touch text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('cookies.savePreferences')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieConsent;

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
